<template>
    <div>
        <!-- Statistiche Rapide -->
        <v-row class="mb-4">
            <v-col cols="12" sm="6" md="3">
                <v-card class="pa-4" color="green lighten-5">
                    <div class="text-h6 green--text text--darken-2">Totale Entrate</div>
                    <div class="text-h4 font-weight-bold green--text text--darken-3">
                        {{ formatCurrency(totalEntrate) }}
                    </div>
                </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
                <v-card class="pa-4" color="red lighten-5">
                    <div class="text-h6 red--text text--darken-2">Totale Uscite</div>
                    <div class="text-h4 font-weight-bold red--text text--darken-3">
                        {{ formatCurrency(totalUscite) }}
                    </div>
                </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
                <v-card class="pa-4" :color="saldoFinale >= 0 ? 'blue lighten-5' : 'orange lighten-5'">
                    <div class="text-h6"
                        :class="saldoFinale >= 0 ? 'blue--text text--darken-2' : 'orange--text text--darken-2'">
                        Saldo Finale
                    </div>
                    <div class="text-h4 font-weight-bold"
                        :class="saldoFinale >= 0 ? 'blue--text text--darken-3' : 'orange--text text--darken-3'">
                        {{ formatCurrency(saldoFinale) }}
                    </div>
                </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
                <v-card class="pa-4" color="purple lighten-5">
                    <div class="text-h6 purple--text text--darken-2">Variazione</div>
                    <div class="text-h4 font-weight-bold purple--text text--darken-3">
                        {{ formatCurrency(variazione) }}
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Grafici -->
        <v-row>
            <!-- Grafico Andamento Saldo -->
            <v-col cols="12" lg="12">
                <v-card>
                    <v-card-title>Andamento Saldo Progressivo</v-card-title>
                    <v-card-text>
                        <canvas ref="lineChart" height="100"></canvas>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Grafico Flussi Mensili -->
            <v-col cols="12" lg="12">
                <v-card>
                    <v-card-title>Flussi Mensili</v-card-title>
                    <v-card-text>
                        <canvas ref="monthlyChart"></canvas>
                    </v-card-text>
                </v-card>
            </v-col>


            <!-- Grafico Entrate vs Uscite -->
            <v-col cols="12" lg="6">
                <v-card>
                    <v-card-title>Entrate vs Uscite</v-card-title>
                    <v-card-text>
                        <canvas ref="pieChart"></canvas>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Grafico a Barre per Tipologia -->
            <v-col cols="12" lg="6">
                <v-card>
                    <v-card-title>Distribuzione per Tipologia</v-card-title>
                    <v-card-text>
                        <canvas ref="barChart"></canvas>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import Chart from 'chart.js';

export default {
    name: "CashflowCharts",

    props: {
        items: {
            type: Array,
            required: true
        },
        startingBalance: {
            type: Number,
            required: true
        },
        startingDate: {
            type: String,
            required: true
        }
    },

    data: () => ({
        charts: {
            line: null,
            pie: null,
            bar: null,
            monthly: null
        }
    }),

    computed: {
        // Le righe "storico" sono solo informative: non entrano né nel saldo né in totali e grafici
        itemsCalcolo() {
            return this.items.filter(item => !item.storico);
        },

        totalEntrate() {
            return this.itemsCalcolo.reduce((sum, item) => sum + (item.escludiCalcolo ? 0 : (item.entrata || 0)), 0).toFixed(3);
        },

        totalUscite() {
            return this.itemsCalcolo.reduce((sum, item) => sum + (item.escludiCalcolo ? 0 : (item.uscita || 0)), 0).toFixed(3);
        },

        saldoFinale() {
            const value = this.itemsCalcolo.length > 0
                ? this.itemsCalcolo[this.itemsCalcolo.length - 1].runningTotal
                : this.startingBalance;

            return Number(Number(value).toFixed(3));
        },

        variazione() {
            return this.saldoFinale - this.startingBalance;
        },

        chartDataByType() {
            const data = { Reale: 0, Previsionale: 0, Banca: 0 };
            this.itemsCalcolo.forEach(item => {
                const amount = item.escludiCalcolo ? 0 : (item.entrata || 0) - (item.uscita || 0);
                data[item.tipologia] = (data[item.tipologia] || 0) + amount;
            });
            return data;
        },

        monthlyData() {
            const months = {};
            this.itemsCalcolo.forEach(item => {
                const date = new Date(item.data);
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

                if (!months[monthKey]) {
                    months[monthKey] = { entrate: 0, uscite: 0 };
                }

                months[monthKey].entrate += item.escludiCalcolo ? 0 : item.entrata || 0;
                months[monthKey].uscite += item.escludiCalcolo ? 0 : item.uscita || 0;
            });

            return Object.keys(months).sort().map(key => ({
                month: this.formatMonth(key),
                entrate: Number(months[key].entrate.toFixed(3)),
                uscite: Number(months[key].uscite.toFixed(3))
            }));
        },
    },

    watch: {
        items: {
            handler() {
                this.$nextTick(() => {
                    this.updateCharts();
                });
            },
            deep: true
        }
    },

    mounted() {
        this.initCharts();
    },

    beforeDestroy() {
        this.destroyCharts();
    },

    methods: {
        initCharts() {
            this.createLineChart();
            this.createPieChart();
            this.createBarChart();
            this.createMonthlyChart();
        },

        updateCharts() {
            this.destroyCharts();
            this.initCharts();
        },

        destroyCharts() {
            Object.values(this.charts).forEach(chart => {
                if (chart) chart.destroy();
            });
        },

        createLineChart() {
            const ctx = this.$refs.lineChart?.getContext('2d');
            if (!ctx) return;

            const labels = this.itemsCalcolo.map(item => this.formatDate(item.data));
            const data = this.itemsCalcolo.map(item => item.runningTotal);

            this.charts.line = new Chart(ctx, {
                type: 'line',
                data: {
                    labels,
                    datasets: [{
                        label: 'Saldo Progressivo',
                        data,
                        borderColor: '#1976D2',
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        tension: 0.4,
                        fill: true,
                        pointBackgroundColor: data.map(val => val >= 0 ? '#4CAF50' : '#F44336'),
                        pointBorderColor: '#fff',
                        pointRadius: 4,
                        pointHoverRadius: 6
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        legend: {
                            display: true,
                            position: 'top'
                        },
                        datalabels: {
                            display: false,
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    const value = context.parsed.y;

                                    const formatted = new Intl.NumberFormat('it-IT', {
                                        style: 'currency',
                                        currency: 'EUR'
                                    }).format(value);

                                    return `Saldo: ${formatted}`;
                                }
                            }
                        }

                    },
                    scales: {
                        y: {
                            ticks: {
                                display: false   // <-- NASCONDE NUMERI Y
                            },
                            grid: {
                                color: 'rgba(0, 0, 0, 0.05)'
                            }
                        },
                        x: {
                            ticks: {
                                display: false   // <-- NASCONDE ETICHETTE X
                            },
                            grid: {
                                display: false
                            }
                        }
                    }
                }

            });
        },

        createPieChart() {
            const ctx = this.$refs.pieChart?.getContext('2d');
            if (!ctx) return;

            this.charts.pie = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: ['Entrate', 'Uscite'],
                    datasets: [{
                        data: [this.totalEntrate, this.totalUscite],
                        backgroundColor: ['#4CAF50', '#F44336'],
                        borderWidth: 2,
                        borderColor: '#fff'
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,

                    plugins: {
                        legend: {
                            position: 'bottom'
                        },

                        datalabels: {
                            color: this.$vuetify.theme.dark ? '#ACACAC' : '#333',
                            anchor: 'end',
                            align: 'end',
                            offset: 8,

                            formatter: (value, ctx) => {
                                return `${this.formatCurrency(value)}`;
                            },

                            font: {
                                weight: 'bold',
                                size: 12
                            }
                        },

                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    const label = context.label || '';
                                    const value = this.formatCurrency(context.parsed);
                                    return `${label}: ${value} `;
                                }
                            }
                        }
                    }
                }

            });
        },

        createBarChart() {
            const ctx = this.$refs.barChart?.getContext('2d');
            if (!ctx) return;

            const data = this.chartDataByType;

            this.charts.bar = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: Object.keys(data),
                    datasets: [{
                        label: 'Saldo per Tipologia',
                        data: Object.values(data),
                        backgroundColor: ['#2196F3', '#FF9800', '#9C27B0'],
                        borderWidth: 0
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        legend: {
                            display: false
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    return `Saldo: ${this.formatCurrency(context.parsed.y)}`;
                                }
                            }
                        },
                        datalabels: {
                            color: this.$vuetify.theme.dark ? '#ACACAC' : '#333',
                            anchor: 'end',
                            align: 'end',
                            offset: 8,

                            formatter: (value, ctx) => {
                                return `${this.formatCurrency(value)}`;
                            },

                            font: {
                                weight: 'bold',
                                size: 12
                            }
                        },
                    },
                    scales: {
                        y: {
                            ticks: {
                                callback: (value) => this.formatCurrency(value)
                            }
                        }
                    }
                }
            });
        },

        createMonthlyChart() {
            const ctx = this.$refs.monthlyChart?.getContext('2d');
            if (!ctx) return;

            const monthlyData = this.monthlyData;

            this.charts.monthly = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: monthlyData.map(d => d.month),
                    datasets: [
                        {
                            label: 'Entrate',
                            data: monthlyData.map(d => d.entrate),
                            backgroundColor: '#4CAF50',
                            borderWidth: 0
                        },
                        {
                            label: 'Uscite',
                            data: monthlyData.map(d => d.uscite),
                            backgroundColor: '#F44336',
                            borderWidth: 0
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        legend: {
                            position: 'top'
                        },
                        datalabels: {
                            color: this.$vuetify.theme.dark ? '#ACACAC' : '#333',
                            anchor: 'end',
                            align: 'end',
                            offset: 8,

                            formatter: (value, ctx) => {
                                return `${this.formatCurrency(value)}`;
                            },

                            font: {
                                weight: 'bold',
                                size: 12
                            }
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    return `${context.dataset.label}: ${this.formatCurrency(context.parsed.y)}`;
                                }
                            }
                        }
                    },
                    scales: {
                        y: {
                            ticks: {
                                callback: (value) => this.formatCurrency(value)
                            },
                            stacked: false
                        },
                        x: {
                            stacked: false
                        }
                    }
                }
            });
        },

        formatCurrency(value) {
            return new Intl.NumberFormat("it-IT", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(value);
        },

        formatDate(isoDate) {
            const [year, month, day] = isoDate.split('-');
            return `${day}/${month}`;
        },

        formatMonth(monthKey) {
            const [year, month] = monthKey.split('-');
            const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
            return `${months[parseInt(month) - 1]} ${year}`;
        },
    }
};
</script>

<style scoped>
.text-h6 {
    font-size: 0.875rem;
    margin-bottom: 8px;
}

.text-h4 {
    font-size: 1.5rem;
}
</style>